import React from "react";

import { observer } from "mobx-react-lite";
import BookingStore from "booking/store/BookingStore";
import { HealthCheckBookingData } from "common";
import { Formik, Field } from "formik";
import * as yup from "yup";
import { InputYesNo } from "core/components/generic/InputYesNo";
import SubmitBookingUseCase from "booking/use-cases/SubmitBookingUseCase";

const HealthCheckInformation = observer(() => {
    const store = BookingStore.get();
    const bookingData = store.bookingData as HealthCheckBookingData;

    const onSubmit = async (values: Partial<HealthCheckBookingData>) => {
        await SubmitBookingUseCase.create().execute({ ...bookingData, ...values });
    };

    return (
        <Formik
            initialValues={{
                bloodThinning: bookingData.bloodThinning,
                fasting: bookingData.fasting,
                pacemaker: bookingData.pacemaker,
                clothing: bookingData.clothing,
                testConsent: bookingData.testConsent,
            }}
            onSubmit={onSubmit}
            validationSchema={yup.object({
                bloodThinning: yup.bool().required("Please choose Yes or No"),
                fasting: yup.bool().required("Please choose Yes or No"),
                pacemaker: yup.bool().required("Please choose Yes or No"),
                clothing: yup.bool().required("Please choose Yes or No"),
                testConsent: yup
                    .bool()
                    .oneOf([true], "You must agree to proceed")
                    .required("Please choose Yes or No"),
            })}
            validateOnChange={true}
        >
            {({ submitForm, setFieldValue, isSubmitting, errors }) => {
                return (
                    <form onSubmit={(e) => e.preventDefault()} className="l-app +y +gap-0">
                        <div className="l-app__item +middle">
                            <div className="u-height-100pc u-pad">
                                <div className="u-flex u-align-items-center u-justify-content-center">
                                    <div className="o-wrap u-width-login">
                                        <div className="u-border u-rounded u-fill-white u-shadow-ambient">
                                            <div className="u-pad-large">
                                                <p className="u-font-bold u-font-xxl u-marg-bottom">
                                                    Health Check questions
                                                </p>
                                                <div
                                                    className={`c-field u-marg-bottom ${
                                                        errors.bloodThinning ? "+error" : ""
                                                    }`}
                                                >
                                                    <Field
                                                        name="bloodThinning"
                                                        component={InputYesNo}
                                                        label="Are you on any blood thinning medications? (We are unable to carry out glucose/cholesterol tests for those on blood thinning medications)."
                                                        required
                                                        onChange={(e) =>
                                                            setFieldValue(
                                                                "bloodThinning",
                                                                e.target.value === "true"
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <div
                                                    className={`c-field u-marg-bottom ${
                                                        errors.fasting ? "+error" : ""
                                                    }`}
                                                >
                                                    <Field
                                                        name="fasting"
                                                        component={InputYesNo}
                                                        label="You must fast for 3 hours before the appointment if you want/are able to have cholesterol/glucose checked (you can drink water) - confirm you understand"
                                                        required
                                                        onChange={(e) =>
                                                            setFieldValue(
                                                                "fasting",
                                                                e.target.value === "true"
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <div
                                                    className={`c-field u-marg-bottom ${
                                                        errors.pacemaker ? "+error" : ""
                                                    }`}
                                                >
                                                    <Field
                                                        name="pacemaker"
                                                        component={InputYesNo}
                                                        label="Do you have a pacemaker fitted? (We are unable to carry out the body comp analysis for those with pacemakers)."
                                                        required
                                                        onChange={(e) =>
                                                            setFieldValue(
                                                                "pacemaker",
                                                                e.target.value === "true"
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <div
                                                    className={`c-field u-marg-bottom ${
                                                        errors.clothing ? "+error" : ""
                                                    }`}
                                                >
                                                    <Field
                                                        name="clothing"
                                                        component={InputYesNo}
                                                        label="You will need to stand barefoot on the body composition machine, so please wear clothes that will easily allow this (e.g. tights can make this difficult) - confirm you understand"
                                                        required
                                                        onChange={(e) =>
                                                            setFieldValue(
                                                                "clothing",
                                                                e.target.value === "true"
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <div
                                                    className={`c-field u-marg-bottom ${
                                                        errors.testConsent ? "+error" : ""
                                                    }`}
                                                >
                                                    <p className="u-font-bold u-font-l u-marg-bottom">
                                                        How we use your information:
                                                    </p>
                                                    <p className="u-marg-bottom">
                                                        By attending an Action Cancer health check,
                                                        you are consenting to our staff carrying out
                                                        these tests. If any of your results are
                                                        outside the recommended ranges we will{" "}
                                                        <u>automatically</u> refer you to your GP
                                                        and send them the results of your health
                                                        check. Sometimes we use/share anonymised
                                                        data with trusted third parties such as{" "}
                                                        <span className="u-font-bold">
                                                            {" "}
                                                            universities or funders
                                                        </span>{" "}
                                                        for service audit and evaluation purposes.
                                                        This helps us demonstrate the value of our
                                                        work and contribute to wider learning.{" "}
                                                        <span className="u-font-bold">
                                                            We never share your personal
                                                            information.
                                                        </span>
                                                    </p>
                                                    <Field
                                                        name="testConsent"
                                                        component={InputYesNo}
                                                        label="I understand how AC will process my information."
                                                        onChange={(e) =>
                                                            setFieldValue(
                                                                "testConsent",
                                                                e.target.value === "true"
                                                            )
                                                        }
                                                        required
                                                    />
                                                </div>
                                                <br />
                                                <div className="u-marg-bottom">
                                                    <p>
                                                        NB - It is not advised to book a health
                                                        check if you are pregnant - results are
                                                        unlikely to be accurate, so could result in
                                                        unnecessary referral to GP.
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="l-app__item +end">
                            <div className="u-pad u-fill-white u-border-top">
                                <div className="u-flex c-footer">
                                    <button
                                        type="button"
                                        className="c-button +quiet u-marg-right u-marg-bottom-small"
                                        onClick={() => {
                                            store.setEnteringPersonalDetails(true);
                                        }}
                                    >
                                        <svg className="c-icon u-font-xl u-marg-right-small">
                                            <use href="/symbol-defs.svg#icon-arrow-left" />
                                        </svg>
                                        Back
                                    </button>

                                    <button
                                        type="submit"
                                        className="c-button u-fill-1 +primary u-marg-bottom-small"
                                        onClick={submitForm}
                                        disabled={isSubmitting}
                                    >
                                        Book Appointment
                                        <svg className="c-icon u-font-xl u-marg-left-small">
                                            <use href="/symbol-defs.svg#icon-check" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                );
            }}
        </Formik>
    );
});

export default HealthCheckInformation;
